import * as React from "react";

interface Props {
    label?: React.ReactNode;
    labelClassName?: string;
}

interface UncontrolledProps {
    initial?: boolean;
}

type CheckboxProps = Props & React.InputHTMLAttributes<HTMLInputElement>;

interface AlignedProps {
    alignment: "center" | "top";
}

const inputClassName =
    "form-checkbox rounded disabled:bg-gray-100 disabled:text-gray-400 border-gray-400 hover:border-brand-600 disabled:hover:border-gray-400 text-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300";

const AlignedCheckbox = React.forwardRef<HTMLInputElement, CheckboxProps & AlignedProps>(
    (props, ref) => {
        const { label, className, labelClassName, alignment, ...rest } = props;
        const top = alignment === "top";
        return (
            <div className={`${className ?? ""} w-full`}>
                <label className={`inline-flex ${top ? "items-start" : "items-center"}`}>
                    <input
                        ref={ref}
                        type="checkbox"
                        className={`${inputClassName} ${top ? "mt-0.5 shrink-0" : ""}`}
                        {...rest}
                    />
                    {label ? (
                        <div className={`ml-2 ${labelClassName ?? ""}`}>{label}</div>
                    ) : null}
                </label>
            </div>
        );
    },
);
AlignedCheckbox.displayName = "AlignedCheckbox";

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
    (props, ref) => <AlignedCheckbox ref={ref} alignment="center" {...props} />,
);
Checkbox.displayName = "Checkbox";

export const TopAlignedCheckbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
    (props, ref) => <AlignedCheckbox ref={ref} alignment="top" {...props} />,
);
TopAlignedCheckbox.displayName = "TopAlignedCheckbox";

export const CheckboxUncontrolled: React.FC<
    Props & UncontrolledProps & React.InputHTMLAttributes<HTMLInputElement>
> = props => {
    const { label, className, initial, onChange, ...rest } = props;
    const [isChecked, setIsChecked] = React.useState(initial);
    const realOnChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setIsChecked(e.target.checked);
            if (onChange) {
                onChange(e);
            }
        },
        [onChange, setIsChecked],
    );
    return (
        <Checkbox
            label={label}
            checked={isChecked}
            onChange={realOnChange}
            className={className}
            {...rest}
        />
    );
};
